import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';
import { Subject } from 'rxjs';
import Fuse from 'fuse.js';
import {
  ClinicalTimelineSearchIndexRecord,
  ClinicalTimelineSearchRawItem,
  buildNormalizedRecords,
  createFuseIndex,
  searchTimelineCorpus
} from './clinical-timeline-search-core';
import type {
  SearchResultMessage,
  TimelineSearchWorkerOutbound
} from './clinical-timeline-search.worker';

const CHUNK_SIZE = 400;

export type TimelineSearchHitPayload = SearchResultMessage['hits'][number];

@Injectable({ providedIn: 'root' })
export class ClinicalTimelineSearchService implements OnDestroy {
  readonly corpusReady$ = new Subject<number>();
  readonly searchHits$ = new Subject<{
    searchId: number;
    corpusId: number;
    hits: TimelineSearchHitPayload[];
  }>();

  private worker: Worker | null = null;
  private activeCorpusId = 0;
  private activeSearchId = 0;
  private fallbackFuse: Fuse<ClinicalTimelineSearchIndexRecord> | null = null;
  private fallbackCorpusId = 0;

  constructor(
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  ngOnDestroy(): void {
    this.worker?.terminate();
    this.worker = null;
    this.corpusReady$.complete();
    this.searchHits$.complete();
  }

  /**
   * Posts corpus to worker in chunks. Returns corpus id for this build (for stale checks).
   */
  setCorpus(items: ClinicalTimelineSearchRawItem[]): number {
    const corpusId = ++this.activeCorpusId;
    const canUseWorker = isPlatformBrowser(this.platformId) && typeof Worker !== 'undefined';

    if (canUseWorker) {
      try {
        const w = this.getWorker();
        const chunks = chunkArray(items, CHUNK_SIZE);
        const toSend = chunks.length > 0 ? chunks : [[]];
        const chunkCount = toSend.length;
        toSend.forEach((chunkItems, idx) => {
          w.postMessage({
            type: 'SET_CORPUS',
            corpusId,
            chunkIndex: idx,
            chunkCount,
            items: chunkItems
          });
        });
        return corpusId;
      } catch {
        /* fall through */
      }
    }

    this.fallbackCorpusId = corpusId;
    const normalized = buildNormalizedRecords(items);
    this.fallbackFuse = normalized.length ? createFuseIndex(normalized) : null;
    this.zone.run(() => {
      this.corpusReady$.next(corpusId);
    });
    return corpusId;
  }

  search(corpusId: number, query: string): number {
    const searchId = ++this.activeSearchId;
    const trimmed = query.trim();
    const canUseWorker = isPlatformBrowser(this.platformId) && typeof Worker !== 'undefined';

    if (canUseWorker && this.worker) {
      try {
        this.worker.postMessage({
          type: 'SEARCH',
          searchId,
          corpusId,
          query: trimmed
        });
        return searchId;
      } catch {
        /* fall through */
      }
    }

    this.runSearchFallback(searchId, corpusId, trimmed);
    return searchId;
  }

  get activeCorpusIdValue(): number {
    return this.activeCorpusId;
  }

  get activeSearchIdValue(): number {
    return this.activeSearchId;
  }

  private runSearchFallback(searchId: number, corpusId: number, query: string): void {
    if (corpusId !== this.fallbackCorpusId || !this.fallbackFuse) {
      this.zone.run(() => {
        this.searchHits$.next({ searchId, corpusId, hits: [] });
      });
      return;
    }
    if (!query) {
      this.zone.run(() => {
        this.searchHits$.next({ searchId, corpusId, hits: [] });
      });
      return;
    }
    const searchHits = searchTimelineCorpus(this.fallbackFuse, query);
    const hits = searchHits.map((h) => ({
      id: h.id,
      record: h.record,
      matches: h.fuseResult.matches
    }));
    this.zone.run(() => {
      this.searchHits$.next({ searchId, corpusId, hits });
    });
  }

  private getWorker(): Worker {
    if (!this.worker) {
      this.worker = new Worker(
        new URL('./clinical-timeline-search.worker', import.meta.url),
        { type: 'module' }
      );
      this.worker.onmessage = (ev: MessageEvent<TimelineSearchWorkerOutbound>) => {
        const d = ev.data;
        if (!d?.type) {
          return;
        }
        if (d.type === 'CORPUS_READY') {
          if (d.corpusId !== this.activeCorpusId) {
            return;
          }
          this.zone.run(() => this.corpusReady$.next(d.corpusId));
        } else if (d.type === 'SEARCH_RESULT') {
          if (d.corpusId !== this.activeCorpusId || d.searchId !== this.activeSearchId) {
            return;
          }
          this.zone.run(() =>
            this.searchHits$.next({
              searchId: d.searchId,
              corpusId: d.corpusId,
              hits: d.hits
            })
          );
        }
      };
      this.worker.onerror = () => {
        /* Worker failed; subsequent setCorpus will retry or use fallback on next call */
      };
    }
    return this.worker;
  }
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  if (!arr.length) {
    return [];
  }
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}
